import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { compressCarrierData } from '../utils/carrierHelpers';
import { confirmDialog } from './ConfirmDialog';

function AdminAgentsComp({ token, filterByStatus = 'active', pageTitle = 'Agents' }) {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editConfig, setEditConfig] = useState({});
  const [yesterdayStats, setYesterdayStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // For active page: all, active, inactive | For pending page: all, pending, declined

  useEffect(() => {
    fetchAgents();
    fetchYesterdayStats();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      // Add accountStatus filter to API call
      const queryParam = filterByStatus ? `?accountStatus=${filterByStatus}` : '';
      console.log(`[AdminAgentsComp] Fetching agents with filter: ${filterByStatus}, query: ${queryParam}`);
      
      const response = await axios.get(`/api/admin/agents/summary${queryParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log(`[AdminAgentsComp] Received ${response.data.length} agents from API`);
      
      // ⭐ CLIENT-SIDE SAFETY FILTER - Double-check filtering based on filterByStatus
      let filteredData = response.data;
      
      if (filterByStatus === 'active') {
        // For active page: EXCLUDE pending agents, show activated OR legacy (null/undefined)
        filteredData = response.data.filter(a => {
          const isPending = a.accountStatus === 'pending';
          return !isPending; // Exclude pending
        });
        console.log(`[AdminAgentsComp] CLIENT FILTER: Filtered ${response.data.length} -> ${filteredData.length} active agents (excluded pending)`);
      } else if (filterByStatus === 'pending') {
        // For pending page: show ONLY pending agents
        filteredData = response.data.filter(a => a.accountStatus === 'pending');
        console.log(`[AdminAgentsComp] CLIENT FILTER: Filtered ${response.data.length} -> ${filteredData.length} pending agents`);
      }
      
      setAgents(filteredData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching agents:', error);
      setLoading(false);
    }
  };

  const fetchYesterdayStats = async () => {
    try {
      // Fetch all leads to calculate yesterday's stats
      const leadsRes = await axios.get('/api/admin/distribution/leads?limit=10000', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const leads = leadsRes.data.leads || [];
      
      // Calculate yesterday in US Eastern timezone
      const now = new Date();
      const yesterdayDate = new Date(now);
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);
      
      const yesterdayParts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(yesterdayDate);
      
      const yesterdayKeyEastern = `${yesterdayParts.find(p => p.type === 'year').value}-${yesterdayParts.find(p => p.type === 'month').value}-${yesterdayParts.find(p => p.type === 'day').value}`;
      
      // Count UNIQUE lead emails per agent for yesterday (deduplication)
      const stats = {};
      const seenEmails = {}; // Track which emails we've already counted per agent
      
      leads.forEach(lead => {
        if (lead.timestamp && lead.leadEmail && lead.agentEmail) {
          // Convert timestamp to US Eastern date
          const leadDate = new Date(lead.timestamp);
          const leadFormatter = new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
          const leadParts = leadFormatter.formatToParts(leadDate);
          const leadDateEastern = `${leadParts.find(p => p.type === 'year').value}-${leadParts.find(p => p.type === 'month').value}-${leadParts.find(p => p.type === 'day').value}`;
          
          if (leadDateEastern === yesterdayKeyEastern) {
            // Initialize tracking for this agent if needed
            if (!seenEmails[lead.agentEmail]) {
              seenEmails[lead.agentEmail] = new Set();
              stats[lead.agentEmail] = 0;
            }
            
            // Only count if we haven't seen this email for this agent yet
            if (!seenEmails[lead.agentEmail].has(lead.leadEmail)) {
              seenEmails[lead.agentEmail].add(lead.leadEmail);
              stats[lead.agentEmail]++;
            }
          }
        }
      });
      
      setYesterdayStats(stats);
    } catch (error) {
      console.error('Error fetching yesterday stats:', error);
    }
  };

  const handleActivateAgent = async (agent) => {
    const confirmed = await confirmDialog(
      `Activate agent ${agent.name} (${agent.email})?\n\nThis will allow them to login and access the system.`
    );
    if (!confirmed) return;

    try {
      await axios.post(`/api/admin/agents/${encodeURIComponent(agent.email)}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Agent ${agent.name} activated successfully!`);
      fetchAgents(); // Refresh the list
    } catch (error) {
      console.error('Error activating agent:', error);
      toast.error(error.response?.data?.error || 'Error activating agent');
    }
  };

  const handleDeclineAgent = async (agent) => {
    const confirmed = await confirmDialog(
      `Decline agent ${agent.name} (${agent.email})?\n\nThis will prevent them from logging in and they will receive an error message on login attempt.`
    );
    if (!confirmed) return;

    try {
      await axios.post(`/api/admin/agents/${encodeURIComponent(agent.email)}/decline`, 
        { reason: 'Declined by admin' }, 
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`Agent ${agent.name} declined successfully!`);
      fetchAgents(); // Refresh the list
    } catch (error) {
      console.error('Error declining agent:', error);
      toast.error(error.response?.data?.error || 'Error declining agent');
    }
  };

  const handleDelete = async (agent) => {
    const displayName = agent.agentType === 'group' 
      ? (agent.ghlLocationName || 'this group')
      : agent.name;
      
    const confirmed = await confirmDialog(`Are you sure you want to delete ${displayName}?`);
    if (!confirmed) return;
    
    try {
      // For groups, use locationId; for individuals, use email
      let url;
      if (agent.agentType === 'group' && agent.ghlLocationId) {
        url = `/api/admin/groups/location/${encodeURIComponent(agent.ghlLocationId)}`;
      } else if (agent.email) {
        url = `/api/admin/agents/${encodeURIComponent(agent.email)}`;
      } else {
        toast.error('Cannot delete: missing identifier');
        return;
      }
      
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAgents();
      toast.success(`${agent.agentType === 'group' ? 'Group' : 'Agent'} deleted successfully`);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error deleting ' + (agent.agentType === 'group' ? 'group' : 'agent'));
    }
  };

  const toggleAgentStatus = async (email, currentStatus) => {
    const confirmed = await confirmDialog(
      `Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this agent?`
    );
    if (!confirmed) return;

    try {
      // ✅ FIX: Fetch full agent data first to preserve all fields (especially statesWithCarriers for groups)
      const fullAgentResponse = await axios.get(`/api/admin/agents/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fullAgent = fullAgentResponse.data;
      
      // Compress group members carrier data to reduce payload size
      let compressedAgent = { ...fullAgent };
      if (fullAgent.groupMembers && fullAgent.groupMembers.length > 0) {
        compressedAgent.groupMembers = fullAgent.groupMembers.map(member => ({
          ...member,
          statesWithCarriers: member.statesWithCarriers ? compressCarrierData(member.statesWithCarriers) : {}
        }));
      }
      
      // Update only the enabled status
      const updatedConfig = {
        ...compressedAgent.distributionConfig,
        enabled: !currentStatus
      };

      await axios.put(`/api/admin/agents/${encodeURIComponent(email)}`, {
        ...compressedAgent,
        distributionConfig: updatedConfig
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Agent ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchAgents();
    } catch (error) {
      console.error('Error updating agent status:', error);
      toast.error('Error updating agent status');
    }
  };

  const startEditing = (agent) => {
    setEditingAgent(agent.email);
    setEditConfig({
      priority: agent.distributionConfig?.priority || 1,
      weightage: agent.distributionConfig?.weightage || 1,
      dailyCap: agent.distributionConfig?.dailyCap || 0,
      monthlyCap: agent.distributionConfig?.monthlyCap || 0,
      totalPurchased: agent.distributionConfig?.totalPurchased || 0
    });
  };

  const cancelEditing = () => {
    setEditingAgent(null);
    setEditConfig({});
  };

  const saveConfig = async (email) => {
    try {
      const agent = agents.find(a => a.email === email);
      
      // ✅ CAP VALIDATION
      const totalPurchased = editConfig.totalPurchased || 0;
      const dailyCap = editConfig.dailyCap || 0;
      const monthlyCap = editConfig.monthlyCap || 0;
      
      if (agent.agentType === 'individual') {
        // Individual agent validation
        if (dailyCap > totalPurchased) {
          toast.error(`Daily cap (${dailyCap}) cannot be greater than total purchased (${totalPurchased})`);
          return;
        }
        if (monthlyCap > totalPurchased) {
          toast.error(`Monthly cap (${monthlyCap}) cannot be greater than total purchased (${totalPurchased})`);
          return;
        }
      } else if (agent.agentType === 'group' && agent.groupMembers && agent.groupMembers.length > 0) {
        // Group validation - sum of member caps
        const totalMemberDailyCap = agent.groupMembers.reduce((sum, m) => sum + (m.dailyCap || 0), 0);
        const totalMemberMonthlyCap = agent.groupMembers.reduce((sum, m) => sum + (m.monthlyCap || 0), 0);
        
        if (totalMemberDailyCap > totalPurchased) {
          toast.error(`Sum of member daily caps (${totalMemberDailyCap}) cannot be greater than total purchased (${totalPurchased})`);
          return;
        }
        if (totalMemberMonthlyCap > totalPurchased) {
          toast.error(`Sum of member monthly caps (${totalMemberMonthlyCap}) cannot be greater than total purchased (${totalPurchased})`);
          return;
        }
      }
      
      // Get the full agent data first to ensure we have all fields
      const fullAgentResponse = await axios.get(`/api/admin/agents/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fullAgent = fullAgentResponse.data;
      
      // Compress group members carrier data to reduce payload size
      let compressedAgent = { ...fullAgent };
      if (fullAgent.groupMembers && fullAgent.groupMembers.length > 0) {
        compressedAgent.groupMembers = fullAgent.groupMembers.map(member => ({
          ...member,
          statesWithCarriers: member.statesWithCarriers ? compressCarrierData(member.statesWithCarriers) : {}
        }));
      }
      
      // Update with the edited config
      await axios.put(`/api/admin/agents/${encodeURIComponent(email)}`, {
        ...compressedAgent,
        distributionConfig: {
          ...compressedAgent.distributionConfig,
          ...editConfig
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Configuration updated successfully');
      setEditingAgent(null);
      setEditConfig({});
      
      // Force refresh to ensure we get latest data
      await fetchAgents();
    } catch (error) {
      console.error('Error updating configuration:', error);
      toast.error('Error updating configuration');
    }
  };

  const getYesterdayStats = (agentEmail) => {
    return yesterdayStats[agentEmail] || 0;
  };

  // Filter agents based on search term and status filter
  const filteredAgents = agents.filter(agent => {
    // First apply status filter based on page context
    if (filterByStatus === 'pending') {
      // For pending page: filter by accountStatus (pending/declined)
      if (statusFilter === 'pending' && agent.accountStatus !== 'pending') return false;
      if (statusFilter === 'declined' && agent.accountStatus !== 'declined') return false;
    } else {
      // For active page: filter by enabled/disabled status
      const config = agent.distributionConfig || {};
      const isEnabled = config.enabled !== false; // Default to true if not set
      
      if (statusFilter === 'active' && !isEnabled) return false;
      if (statusFilter === 'inactive' && isEnabled) return false;
    }
    
    // Then apply search filter
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase().trim();
    
    // 🔥 If search term contains comma, search ONLY by multiple states
    if (term.includes(',')) {
      // Split by comma, clean up whitespace, remove empty strings, convert to uppercase
      const searchStates = term
        .split(',')
        .map(s => s.trim().toUpperCase())
        .filter(s => s.length > 0);
      
      // Check if agent has any of the searched states
      if (agent.states && agent.states.length > 0) {
        return agent.states.some(state => 
          searchStates.includes(state.toUpperCase())
        );
      }
      return false;
    }
    
    // Normal search (no comma) - search all fields
    // Search by agent name
    if (agent.name && agent.name.toLowerCase().includes(term)) return true;
    
    // Search by agent email
    if (agent.email && agent.email.toLowerCase().includes(term)) return true;
    
    // Search by GHL location name (for groups)
    if (agent.ghlLocationName && agent.ghlLocationName.toLowerCase().includes(term)) return true;
    
    // Search by single state code using the states array from summary API
    // Check if the search term matches any of the agent's states (case-insensitive)
    if (agent.states && agent.states.some(state => state.toLowerCase() === term)) return true;
    
    return false;
  });

  if (loading && agents.length === 0) return <div className="loading">Loading agents...</div>;

  return (
    <div className="container">
      <div className="section-header">
        <h2>{pageTitle} ({filteredAgents.length}{searchTerm && ` of ${agents.length}`})</h2>
        {filterByStatus !== 'pending' && (
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/admin/agents/new')}
          >
            + Add New Agent
          </button>
        )}
      </div>

      {/* Filters Section */}
      <div style={{ marginTop: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '12px 16px',
              fontSize: '14px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              outline: 'none',
              background: 'white',
              cursor: 'pointer',
              minWidth: '150px'
            }}
          >
            {filterByStatus === 'pending' ? (
              <>
                <option value="all">All Requests</option>
                <option value="pending">Pending Only</option>
                <option value="declined">Declined Only</option>
              </>
            ) : (
              <>
                <option value="all">All Agents</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </>
            )}
          </select>
          
          <input
            type="text"
            placeholder="Search by agent name, email, location name, or state (e.g. VA, TX, FL)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              padding: '12px 16px',
              fontSize: '14px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          />
        </div>
        {searchTerm && (
          <div style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
            Found {filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''}
            <button
              onClick={() => setSearchTerm('')}
              style={{
                marginLeft: '12px',
                padding: '4px 12px',
                fontSize: '12px',
                background: '#f3f4f6',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      <div className="agent-list" style={filterByStatus === "pending" ? {display: "block"}: {}}>
        {filteredAgents.length === 0 ? (
          <div className="empty-state">
            {searchTerm ? `No agents matching "${searchTerm}"` : 'No agents yet. Create your first agent!'}
          </div>
        ) : (
          filteredAgents.map(agent => {
            const config = agent.distributionConfig || {};
            const isEnabled = config.enabled || false;
            const isEditing = editingAgent === agent.email;
            
            return (
              <div key={agent.email} className="agent-card" style={{ marginBottom: '16px', display: 'block' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h4 style={{ margin: 0 }}>{agent.agentType === 'group' ? agent.ghlLocationName || 'Group Agent' : agent.name}</h4>
                      
                      {/* ⭐ PENDING ACTIVATION BADGE */}
                      {agent.accountStatus === 'pending' && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          color: 'white',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                          animation: 'pulse 2s ease-in-out infinite'
                        }}>
                          <span>⏳</span>
                          <span>Pending Activation</span>
                        </span>
                      )}
                      
                      {/* ⭐ GROUP BADGE */}
                      {agent.agentType === 'group' && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          background: 'linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)',
                          color: 'white',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 4px rgba(147, 51, 234, 0.3)'
                        }}>
                          <span>👥</span>
                          <span>GROUP</span>
                          <span style={{
                            background: 'rgba(255,255,255,0.3)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px'
                          }}>
                            {agent.groupMembers?.length || 0} members
                          </span>
                        </span>
                      )}
                      
                      <label style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        position: 'relative'
                      }}>
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={() => toggleAgentStatus(agent.email, isEnabled)}
                          style={{ display: 'none' }}
                        />
                        <div style={{
                          width: '52px',
                          height: '28px',
                          background: isEnabled ? '#10b981' : '#9ca3af',
                          borderRadius: '14px',
                          position: 'relative',
                          transition: 'background 0.3s',
                          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            background: 'white',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: isEnabled ? '26px' : '2px',
                            transition: 'left 0.3s',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                        <span style={{ 
                          marginLeft: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: isEnabled ? '#10b981' : '#6b7280'
                        }}>
                          {isEnabled ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                    </div>
                    
                    {/* ⭐ Don't show email/NPN for groups - they use locationId */}
                    {agent.agentType !== 'group' && (
                      <>
                        <p className="agent-email">{agent.email}</p>
                        <p className="agent-npn">NPN: {agent.npn}</p>
                        
                        {/* ⭐ ONBOARDING STEP TAGS - Small individual badges with clear text */}
                        {agent.accountStatus === 'pending' && agent.onboarding && (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                            {agent.onboarding.stepsCompleted?.step1 && (
                              <span style={{
                                backgroundColor: '#dbeafe',
                                color: '#1e40af',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: '500',
                                border: '1px solid #93c5fd'
                              }}>
                                Watched Video
                              </span>
                            )}
                            {agent.onboarding.stepsCompleted?.step2 && (
                              <span style={{
                                backgroundColor: '#f3e8ff',
                                color: '#6b21a8',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: '500',
                                border: '1px solid #d8b4fe'
                              }}>
                                Viewed PDF
                              </span>
                            )}
                            {agent.onboarding.stepsCompleted?.step3 && (
                              <span style={{
                                backgroundColor: '#ffedd5',
                                color: '#c2410c',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: '500',
                                border: '1px solid #fed7aa'
                              }}>
                                Setup Config
                              </span>
                            )}
                            {agent.onboarding.stepsCompleted?.step4 && (
                              <span style={{
                                backgroundColor: '#d1fae5',
                                color: '#065f46',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: '500',
                                border: '1px solid #6ee7b7'
                              }}>
                                Filled Onboarding Form
                              </span>
                            )}
                            {!agent.onboarding.completed && (
                              <span style={{
                                backgroundColor: '#f3f4f6',
                                color: '#4b5563',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: '500',
                                border: '1px solid #d1d5db'
                              }}>
                                Step {agent.onboarding.currentStep || 1}/4
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* ⭐ Show GHL Location for groups instead */}
                    {agent.agentType === 'group' && agent.ghlLocationId && (
                      <p style={{ 
                        fontSize: '13px', 
                        color: '#6b7280',
                        fontFamily: 'monospace',
                        background: '#f3f4f6',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'inline-block',
                        marginTop: '4px'
                      }}>
                        📍 Location: {agent.ghlLocationId}
                      </p>
                    )}
                    
                    {/* ⭐ SHOW GROUP MEMBERS - IMPROVED UI */}
                    {agent.agentType === 'group' && agent.groupMembers && agent.groupMembers.length > 0 && (
                      <div style={{
                        marginTop: '16px',
                        padding: '16px',
                        background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                        border: '2px solid #d8b4fe',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(139, 92, 246, 0.1)'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '12px'
                        }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '700',
                            color: '#7c3aed',
                            textTransform: 'uppercase',
                            letterSpacing: '0.8px'
                          }}>
                            👥 GROUP MEMBERS
                          </div>
                          <div style={{
                            background: '#7c3aed',
                            color: 'white',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '700'
                          }}>
                            {agent.groupMembers.length}
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {agent.groupMembers.map((member, idx) => (
                            <div key={member.email} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '12px 14px',
                              background: 'white',
                              border: '1.5px solid #e9d5ff',
                              borderRadius: '8px',
                              transition: 'all 0.2s',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#c084fc';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(192, 132, 252, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#e9d5ff';
                              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                            }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ 
                                  fontWeight: '600', 
                                  color: '#1f2937',
                                  fontSize: '14px',
                                  marginBottom: '4px'
                                }}>
                                  {member.name}
                                </div>
                                <div style={{ 
                                  fontSize: '12px', 
                                  color: '#6b7280',
                                  fontFamily: 'monospace',
                                  marginBottom: '4px'
                                }}>
                                  {member.email}
                                </div>
                              </div>
                              <div style={{
                                display: 'flex',
                                gap: '12px',
                                fontSize: '11px',
                                color: '#6b7280',
                                alignItems: 'center'
                              }}>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '2px' }}>Today</div>
                                  <div style={{ fontWeight: '700', color: '#0891b2', fontSize: '14px' }}>{member.dailySent || 0}</div>
                                </div>
                                <div style={{ 
                                  width: '1px', 
                                  height: '30px', 
                                  background: '#e5e7eb' 
                                }} />
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '2px' }}>Yesterday</div>
                                  <div style={{ fontWeight: '700', color: '#0891b2', fontSize: '14px' }}>{member.yesterdaySent || 0}</div>
                                </div>
                                <div style={{ 
                                  width: '1px', 
                                  height: '30px', 
                                  background: '#e5e7eb' 
                                }} />
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '2px' }}>Priority</div>
                                  <div style={{ fontWeight: '700', color: '#4f46e5' }}>{member.priority || 1}</div>
                                </div>
                                <div style={{ 
                                  width: '1px', 
                                  height: '30px', 
                                  background: '#e5e7eb' 
                                }} />
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '2px' }}>Weight</div>
                                  <div style={{ fontWeight: '700', color: '#7c3aed' }}>{member.weightage || 1}</div>
                                </div>
                                <div style={{ 
                                  width: '1px', 
                                  height: '30px', 
                                  background: '#e5e7eb' 
                                }} />
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '2px' }}>Status</div>
                                  <div style={{
                                    color: member.enabled ? '#059669' : '#dc2626',
                                    fontWeight: '700',
                                    fontSize: '12px'
                                  }}>
                                    {member.enabled ? '✓ Active' : '✗ Inactive'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {agent.states && agent.states.length > 0 && (
                      <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: '4px', 
                        marginTop: '8px',
                        maxWidth: '400px'
                      }}>
                        {agent.states.map(state => (
                          <span
                            key={state}
                            style={{
                              display: 'inline-block',
                              padding: '2px 6px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: '#e0e7ff',
                              color: '#4338ca',
                              borderRadius: '4px',
                              lineHeight: '1.4'
                            }}
                          >
                            {state}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {/* ⭐ ACTIVATE BUTTON (only for pending agents) */}
                    {agent.accountStatus === 'pending' && (
                      <>
                        <button 
                          className="btn btn-sm btn-success"
                          onClick={() => handleActivateAgent(agent)}
                          style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            border: 'none',
                            fontWeight: '600',
                            boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
                          }}
                        >
                          ✅ Activate
                        </button>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeclineAgent(agent)}
                          style={{
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            border: 'none',
                            fontWeight: '600',
                            boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
                          }}
                        >
                          ❌ Decline
                        </button>
                      </>
                    )}
                    
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        // ⭐ For groups, navigate by ghlLocationId; for individuals, use email
                        if (agent.agentType === 'group' && agent.ghlLocationId) {
                          navigate(`/admin/agents/edit/group/${encodeURIComponent(agent.ghlLocationId)}`);
                        } else {
                          navigate(`/admin/agents/edit/${encodeURIComponent(agent.email)}`);
                        }
                      }}
                    >
                      Edit Details
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(agent)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                  
                  <div className="agent-stats" style={{ marginTop: '12px' }}>
                    <span className="badge">
                      {agent.stateCount} State{agent.stateCount !== 1 ? 's' : ''}
                    </span>
                    <span className="badge">
                      {agent.carrierCount} Carrier{agent.carrierCount !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {isEnabled && (
                    <>
                      <div style={{ 
                        marginTop: '16px',
                        padding: '16px',
                        background: '#f0f9ff',
                        borderRadius: '8px',
                        border: '1px solid #bae6fd'
                      }}>
                        <h5 style={{ 
                          margin: '0 0 12px 0', 
                          fontSize: '13px', 
                          fontWeight: '700',
                          color: '#0369a1',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Opportunity Statistics
                        </h5>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                          gap: '12px',
                          fontSize: '13px'
                        }}>
                          <div>
                            <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '11px' }}>Today</div>
                            <div style={{ fontWeight: '700', color: '#0369a1', fontSize: '18px' }}>
                              {config.dailySent || 0}
                            </div>
                          </div>
                          <div>
                            <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '11px' }}>Yesterday</div>
                            <div style={{ fontWeight: '700', color: '#0369a1', fontSize: '18px' }}>
                              {getYesterdayStats(agent.email)}
                            </div>
                          </div>
                          <div>
                            <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '11px' }}>
                              Total Sent{agent.ghlLocationId && ' 🌐'}
                            </div>
                            <div style={{ fontWeight: '700', color: '#0369a1', fontSize: '18px' }}>
                              {agent.ghlContactCount !== undefined ? agent.ghlContactCount : (config.totalSent || 0)}
                            </div>
                            {agent.ghlLocationId && agent.ghlContactCount !== undefined && (
                              <div style={{ fontSize: '9px', color: '#10b981', marginTop: '2px', fontWeight: '500' }}>
                                ✓ DB-cached
                              </div>
                            )}
                          </div>
                          <div>
                            <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '11px' }}>Purchased</div>
                            <div style={{ fontWeight: '700', color: '#059669', fontSize: '18px' }}>
                              {config.totalPurchased || 0}
                            </div>
                          </div>
                          {agent.bypassedLeadsCount > 0 && (
                            <div>
                              <div style={{ color: '#6b7280', marginBottom: '4px', fontSize: '11px' }}>⚠️ Bypassed</div>
                              <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '18px' }}>
                                {agent.bypassedLeadsCount}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {isEditing ? (
                        <div style={{ 
                          marginTop: '16px',
                          padding: '16px',
                          background: '#fef3c7',
                          borderRadius: '8px',
                          border: '2px solid #fbbf24'
                        }}>
                          <h5 style={{ 
                            margin: '0 0 12px 0', 
                            fontSize: '13px', 
                            fontWeight: '700',
                            color: '#92400e',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Edit Configuration
                          </h5>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: '12px'
                          }}>
                            <div>
                              <label style={{ fontSize: '11px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                                Priority
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={editConfig.priority}
                                onChange={(e) => setEditConfig({...editConfig, priority: parseInt(e.target.value) || 1})}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                                Weightage
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={editConfig.weightage}
                                onChange={(e) => setEditConfig({...editConfig, weightage: parseInt(e.target.value) || 1})}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                                Daily Cap
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={editConfig.dailyCap}
                                onChange={(e) => setEditConfig({...editConfig, dailyCap: parseInt(e.target.value) || 0})}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                                Monthly Cap
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={editConfig.monthlyCap}
                                onChange={(e) => setEditConfig({...editConfig, monthlyCap: parseInt(e.target.value) || 0})}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>
                                Total Purchased
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={editConfig.totalPurchased}
                                onChange={(e) => setEditConfig({...editConfig, totalPurchased: parseInt(e.target.value) || 0})}
                                style={{ width: '100%', padding: '6px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px' }}
                              />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                            <button 
                              className="btn btn-sm btn-secondary"
                              onClick={cancelEditing}
                            >
                              Cancel
                            </button>
                            <button 
                              className="btn btn-sm btn-success"
                              onClick={() => saveConfig(agent.email)}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ 
                          marginTop: '16px',
                          padding: '12px 16px',
                          background: '#f9fafb',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <div style={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                              gap: '16px',
                              flex: 1,
                              fontSize: '12px'
                            }}>
                              <div>
                                <div style={{ color: '#6b7280', marginBottom: '4px' }}>Priority</div>
                                <div style={{ fontWeight: '700', fontSize: '16px' }}>{config.priority || 1}</div>
                              </div>
                              <div>
                                <div style={{ color: '#6b7280', marginBottom: '4px' }}>Weightage</div>
                                <div style={{ fontWeight: '700', fontSize: '16px' }}>{config.weightage || 1}</div>
                              </div>
                              <div>
                                <div style={{ color: '#6b7280', marginBottom: '4px' }}>Daily Cap</div>
                                <div style={{ fontWeight: '700', fontSize: '16px' }}>
                                  {config.dailySent || 0} / {config.dailyCap || 0}
                                </div>
                              </div>
                              <div>
                                <div style={{ color: '#6b7280', marginBottom: '4px' }}>Monthly Cap</div>
                                <div style={{ fontWeight: '700', fontSize: '16px' }}>
                                  {config.monthlySent || 0} / {config.monthlyCap || 0}
                                </div>
                              </div>
                            </div>
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => startEditing(agent)}
                              style={{ marginLeft: '16px', whiteSpace: 'nowrap' }}
                            >
                              ⚙️ Edit Config
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default AdminAgentsComp;

// Add keyframe animation for pulse effect
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`;
document.head.appendChild(style);
